'use client';
import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Link from 'next/link';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { AnimateWrapper } from '@overlapdev/wrappers';
import { PageType } from '@overlapdev/types';
import { FeatureCollection, Point } from 'geojson';
import LocationMapPopup from './popup';

mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOXGL_ACCESS_TOKEN || '';

const LocationMapMap = ({ className, locations }: { className?: string; locations?: PageType[] }) => {
	//__Refs
	const map = useRef<mapboxgl.Map | null>(null);
	const mapContainer = useRef(null);

	//__State
	const [supported, setSupported] = useState(true);
	const [popup, setPopup] = useState<mapboxgl.Popup | null>(null);
	const [loaded, setLoaded] = useState(false);

	//__Functions

	//__Use Effect
	useEffect(() => {
		if (!mapboxgl.supported()) {
			setSupported(false);
			return;
		}

		if (map.current) return;
		if (mapContainer?.current) {
			map.current = new mapboxgl.Map({
				container: mapContainer?.current || '',
				style: 'mapbox://styles/mapbox/standard',
				center: [-80.843124, 35.2216],
				zoom: 12,
				pitch: 0,
				bearing: 0,
				cooperativeGestures: true
			});

			map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }));

			const locations_source: FeatureCollection<Point> = { type: 'FeatureCollection', features: [] };

			map.current.loadImage('/img/map-pin.png', (error, image) => {
				if (error || !image) throw error;
				map.current?.addImage('custom-marker-icon', image);
			});

			setPopup(new mapboxgl.Popup({ maxWidth: 'none', offset: 0 }));

			map.current.on('load', () => {
				if (map?.current) {
					//Property Source
					map.current.addSource('markers', {
						type: 'geojson',
						data: locations_source,
						cluster: true,
						clusterMaxZoom: 17,
						clusterRadius: 50
					});

					//Property Markers
					map.current.addLayer({
						id: 'clusters-shadow',
						type: 'circle',
						source: 'markers',
						filter: ['has', 'point_count'],
						paint: {
							'circle-color': '#000',
							'circle-blur': 0.5,
							'circle-radius': ['step', ['get', 'point_count'], 35, 20, 45]
						}
					});
					map.current.addLayer({
						id: 'clusters',
						type: 'circle',
						source: 'markers',
						filter: ['has', 'point_count'],
						paint: {
							'circle-color': '#FF671F',
							'circle-radius': ['step', ['get', 'point_count'], 30, 20, 40]
						}
					});
					map.current.addLayer({
						id: 'cluster-count',
						type: 'symbol',
						source: 'markers',
						filter: ['has', 'point_count'],
						layout: {
							'text-field': ['get', 'point_count_abbreviated'],
							'text-font': ['neue-haas-grotesk-display', 'Arial Unicode MS Regular'],
							'text-size': 22,
							'text-allow-overlap': true
						}
					});
					map.current.addLayer({
						id: 'unclustered-point',
						type: 'symbol',
						source: 'markers',
						filter: ['!', ['has', 'point_count']],
						layout: {
							'icon-image': 'custom-marker-icon',
							'icon-anchor': 'bottom',
							'icon-allow-overlap': true
						}
					});

					const map_canvas = map.current.getCanvas();

					map.current.on('mouseenter', 'clusters', () => {
						map_canvas.style.cursor = 'pointer';
					});
					map.current.on('mouseleave', 'clusters', () => {
						map_canvas.style.cursor = '';
					});

					map.current.on('mouseenter', 'unclustered-point', () => {
						map_canvas.style.cursor = 'pointer';
					});
					map.current.on('mouseleave', 'unclustered-point', () => {
						map_canvas.style.cursor = '';
					});

					// Inspect a cluster on click
					map.current.on('click', 'clusters', (e: any) => {
						popup?.remove();
						const features = map.current?.queryRenderedFeatures(e.point, {
							layers: ['clusters']
						});
						if (features) {
							const cluster_id = features[0].properties?.cluster_id;
							const geometry = features[0].geometry;
							const markers_source = map.current?.getSource('markers') as mapboxgl.GeoJSONSource;
							markers_source?.getClusterExpansionZoom(cluster_id, (err?: Error | null, zoom?: number | null) => {
								if (err) return;
								map.current?.easeTo({
									center: (geometry as any).coordinates,
									zoom: zoom || 12
								});
							});
						}
					});
				}
				setLoaded(true);
			});
		}
	}, [mapContainer]);

	useEffect(() => {
		if (!map.current) return;

		const markers_source = map.current.getSource('markers') as mapboxgl.GeoJSONSource;

		if (markers_source) {
			const bounds = new mapboxgl.LngLatBounds();
			const filtered_markers: FeatureCollection<Point> = { type: 'FeatureCollection', features: [] };

			locations?.map((item) => {
				const { acf } = item || {};
				const { address } = acf || {};
				if (address.lng && address.lat) {
					filtered_markers.features?.push({
						id: item.id,
						type: 'Feature',
						geometry: {
							type: 'Point',
							coordinates: [parseFloat(address.lng.toString()), parseFloat(address.lat.toString())]
						},
						properties: {
							id: item.id
						}
					});
					bounds.extend([parseFloat(address.lng.toString()), parseFloat(address.lat.toString())]);
				}
			});

			markers_source.setData(filtered_markers);

			if (locations && locations?.length > 0 && !bounds.isEmpty()) {
				map.current?.fitBounds(bounds, { padding: 60, maxZoom: 12 });
			}
		}

		//Markers popups
		map.current.on('click', 'unclustered-point', (e: any) => {
			e.preventDefault();
			const features = map.current?.queryRenderedFeatures(e.point, {
				layers: ['unclustered-point']
			});
			if (features?.length) {
				const feature = features[0];
				const geometry = feature.geometry;

				//First remove any previously opened popups
				popup?.remove();
				const item = locations?.find((e) => e.id === feature.properties?.id);
				if (item) {
					const popup_el = document.createElement('div');
					popup_el.className = 'popup';
					createRoot(popup_el).render(<LocationMapPopup item={item} />);

					const coordinates = (geometry as any).coordinates.slice();
					popup?.setOffset(34);
					if (map.current) {
						popup?.setLngLat(coordinates).setDOMContent(popup_el).addTo(map.current);
					}
				}
			}
		});
	}, [locations, loaded]);

	useEffect(() => {
		if (map?.current) {
			map.current.resize();
		}
	}, [className]);

	return (
		<AnimateWrapper animate_type='fade' element='div' className={`relative ${className}`}>
			{supported ? (
				<div className='h-200 overflow-hidden relative' ref={mapContainer} />
			) : (
				<div className='text-center'>
					<h4>Your browser does not support Mapbox GL</h4>
					<p>
						You will need to{' '}
						<Link href='https://get.webgl.org/' target='_blank'>
							enable WebGL
						</Link>{' '}
						in your browser.
					</p>
				</div>
			)}
		</AnimateWrapper>
	);
};

export default LocationMapMap;
